export const PLATFORM_FEE_RATE = 0.05

/** Stripe's fixed US card fee, added once per PaymentIntent charge. */
export const STRIPE_FIXED_FEE_CENTS = 30

/** Stripe standard US card processing — 2.9% is not passed through in charge math. */
export const STRIPE_PROCESSING_FEE_LABEL = '2.9% + $0.30'

export function stripeProcessingFeeLabel(paymentMode = 'full') {
  const fixedCents = stripeFixedFeesTotalCents(paymentMode)
  const fixedAmount = (fixedCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `2.9% + ${fixedAmount}`
}

export function stripeFixedFeeCount(paymentMode) {
  if (paymentMode === 'full') return 1
  if (paymentMode === 'deposit') return 2
  return 0
}

export function stripeFixedFeesTotalCents(paymentMode) {
  return STRIPE_FIXED_FEE_CENTS * stripeFixedFeeCount(paymentMode)
}

export function platformFeeCents(amountCents) {
  if (typeof amountCents !== 'number') return 0
  return Math.round(amountCents * PLATFORM_FEE_RATE)
}

/** Platform fee line shown to customers (5% + Stripe fixed fees for the payment mode). */
export function displayPlatformFeeCents(amountCents, paymentMode = 'deposit') {
  if (typeof amountCents !== 'number') return 0
  if (paymentMode !== 'deposit' && paymentMode !== 'full') return 0
  return platformFeeCents(amountCents) + stripeFixedFeesTotalCents(paymentMode)
}

export function inferDepositTimeSurchargeCents(
  amountCents,
  paidAmountCents,
  paymentMode = 'deposit'
) {
  if (paymentMode !== 'deposit') return 0
  if (typeof paidAmountCents !== 'number') return 0
  return Math.max(0, paidAmountCents - depositDueCents(amountCents, 0))
}

/** Surcharges already collected on the deposit (webhook field or inferred from paid amount). */
export function effectiveDepositTimeSurchargeCents(
  amountCents,
  depositTimeSurchargeCents = 0,
  paidAmountCents,
  paymentMode = 'deposit'
) {
  return Math.max(
    typeof depositTimeSurchargeCents === 'number' ? depositTimeSurchargeCents : 0,
    inferDepositTimeSurchargeCents(amountCents, paidAmountCents, paymentMode)
  )
}

/** Merge pending, saved, and deposit-paid time surcharges for display totals. */
export function effectiveReservationSurchargeCents(
  currentSurchargeCents = 0,
  savedSurchargeCents = 0,
  options = {}
) {
  const {
    paymentMode,
    depositPaid = false,
    depositTimeSurchargeCents = 0,
    paidAmountCents,
    amountCents = 0,
  } = options

  let cents = Math.max(
    typeof currentSurchargeCents === 'number' ? currentSurchargeCents : 0,
    typeof savedSurchargeCents === 'number' ? savedSurchargeCents : 0
  )

  if (depositPaid && paymentMode === 'deposit') {
    cents = Math.max(
      cents,
      typeof depositTimeSurchargeCents === 'number'
        ? depositTimeSurchargeCents
        : 0,
      inferDepositTimeSurchargeCents(
        amountCents,
        paidAmountCents,
        paymentMode
      )
    )
  }

  return cents
}

/** Base for the 5% platform fee: stay amount + business time surcharges. */
export function platformFeeBaseCents(amountCents, timeSurchargeCents = 0) {
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  return base + surcharges
}

export function reservationTotalCents(
  amountCents,
  timeSurchargeCents = 0,
  paymentMode = 'deposit'
) {
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  // No-payment reservations record a reference total only — never pass fees through.
  if (paymentMode === 'none') {
    return base + surcharges
  }
  const feeBase = platformFeeBaseCents(base, surcharges)
  return (
    feeBase +
    platformFeeCents(feeBase) +
    stripeFixedFeesTotalCents(paymentMode)
  )
}

export function depositDueCents(amountCents, timeSurchargeCents = 0) {
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  const feeBase = platformFeeBaseCents(base, surcharges)
  return (
    Math.round(base * 0.5) +
    platformFeeCents(feeBase) +
    surcharges +
    stripeFixedFeesTotalCents('deposit')
  )
}

export function balanceDueCents(
  amountCents,
  timeSurchargeCents = 0,
  options = {}
) {
  const { depositPaid = false, depositTimeSurchargeCents = 0 } = options
  const paymentMode = options.paymentMode || 'deposit'
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  const half = Math.round(base * 0.5)

  if (!depositPaid) {
    return half
  }

  const paidSurcharges = effectiveDepositTimeSurchargeCents(
    base,
    depositTimeSurchargeCents,
    options.paidAmountCents,
    paymentMode
  )
  const additionalSurcharges = Math.max(0, surcharges - paidSurcharges)
  return half + additionalSurcharges
}

export function initialDepositChargeCents(
  paymentMode,
  amountCents,
  timeSurchargeCents = 0
) {
  if (paymentMode === 'deposit') {
    return depositDueCents(amountCents, timeSurchargeCents)
  }
  return reservationTotalCents(amountCents, timeSurchargeCents, 'full')
}

/**
 * Remaining balance for deposit reservations after the deposit is paid.
 * Derived from reservation total minus paid deposit so surcharges on the
 * deposit are never counted again on the balance.
 */
export function reservationBalanceDueCents(
  amountCents,
  timeSurchargeCents = 0,
  options = {}
) {
  const {
    depositPaid = false,
    paymentMode = 'deposit',
    depositTimeSurchargeCents = 0,
    paidAmountCents,
    savedTimeSurchargeCents = 0,
  } = options

  if (!depositPaid || paymentMode !== 'deposit') {
    return balanceDueCents(amountCents, timeSurchargeCents, {
      depositPaid,
      depositTimeSurchargeCents,
      paidAmountCents,
      paymentMode,
    })
  }

  const surcharges = effectiveReservationSurchargeCents(
    timeSurchargeCents,
    savedTimeSurchargeCents,
    {
      paymentMode,
      depositPaid: true,
      depositTimeSurchargeCents,
      paidAmountCents,
      amountCents,
    }
  )
  const total = reservationTotalCents(amountCents, surcharges, paymentMode)
  const paidDeposit =
    paidAmountCents ?? depositDueCents(amountCents, surcharges)
  return Math.max(0, total - paidDeposit)
}
